import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  adminListContacts,
  adminUpdateContactStatus,
  adminDeleteContact,
} from "@/lib/adminInbox.functions";
import { Button } from "@/components/ui/button";
import { Trash2, Mail, Phone, MapPin, Search } from "lucide-react";

export const Route = createFileRoute("/tools/contacts")({
  head: () => ({
    meta: [
      { title: "Contact Submissions | Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ContactsInbox,
});

type Row = Awaited<ReturnType<typeof adminListContacts>>[number];
type StatusFilter = "all" | "new" | "contacted" | "closed";

const STATUS_STYLES: Record<string, string> = {
  new: "bg-primary/10 text-primary",
  contacted: "bg-amber-500/10 text-amber-600",
  closed: "bg-muted text-muted-foreground",
};

function ContactsInbox() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);

  async function load() {
    try {
      const data = await adminListContacts();
      setRows(data);
      if (selected) {
        const fresh = data.find((r) => r.id === selected.id);
        setSelected(fresh ?? null);
      }
    } catch (e: any) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!needle) return true;
      return (
        r.name.toLowerCase().includes(needle) ||
        r.email.toLowerCase().includes(needle) ||
        (r.city ?? "").toLowerCase().includes(needle) ||
        r.message.toLowerCase().includes(needle)
      );
    });
  }, [rows, filter, q]);

  const counts = useMemo(() => {
    const base = { all: 0, new: 0, contacted: 0, closed: 0 };
    (rows ?? []).forEach((r) => {
      base.all++;
      if (r.status in base) (base as any)[r.status]++;
    });
    return base;
  }, [rows]);

  async function setStatus(id: string, status: "new" | "contacted" | "closed") {
    await adminUpdateContactStatus({ data: { id, status } });
    load();
  }

  async function remove(id: string) {
    if (!confirm("Delete this submission? This cannot be undone.")) return;
    await adminDeleteContact({ data: { id } });
    if (selected?.id === id) setSelected(null);
    load();
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold">Contact submissions</h1>
        <div className="flex items-center gap-2">
          {(["all", "new", "contacted", "closed"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-md border px-3 py-1.5 text-xs capitalize transition ${
                filter === s
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, city, message…"
          className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-foreground/40"
        />
      </div>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="overflow-hidden rounded-lg border border-border">
          {!rows ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No submissions match.</p>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelected(r)}
                    className={`flex w-full flex-col items-start gap-1 p-4 text-left transition hover:bg-accent ${
                      selected?.id === r.id ? "bg-accent" : ""
                    }`}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="font-medium">{r.name}</span>
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                          STATUS_STYLES[r.status] ?? "bg-muted text-muted-foreground"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                    <div className="line-clamp-2 text-xs text-muted-foreground">
                      {r.message}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-border p-5">
          {!selected ? (
            <p className="text-sm text-muted-foreground">
              Select a submission to view details.
            </p>
          ) : (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl font-semibold">{selected.name}</h2>
                  <span
                    className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                      STATUS_STYLES[selected.status] ?? "bg-muted text-muted-foreground"
                    }`}
                  >
                    {selected.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(selected.created_at).toLocaleString()}
                </p>
              </div>

              <div className="grid gap-2 text-sm">
                <a
                  href={`mailto:${selected.email}`}
                  className="flex items-center gap-2 text-brand-text underline underline-offset-2"
                >
                  <Mail className="h-4 w-4" /> {selected.email}
                </a>
                {selected.phone && (
                  <a
                    href={`tel:${selected.phone}`}
                    className="flex items-center gap-2 text-brand-text underline underline-offset-2"
                  >
                    <Phone className="h-4 w-4" /> {selected.phone}
                  </a>
                )}
                {selected.city && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" /> {selected.city}
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Message
                </h3>
                <p className="whitespace-pre-wrap rounded-md bg-muted/30 p-3 text-sm">
                  {selected.message}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
                {(["new", "contacted", "closed"] as const).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={selected.status === s ? "default" : "outline"}
                    onClick={() => setStatus(selected.id, s)}
                    className="capitalize"
                  >
                    {s}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(selected.id)}
                  className="ml-auto text-destructive hover:text-destructive"
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Delete
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
